import { AxiosResponse } from 'axios';
import { QueryClient } from '@tanstack/react-query';
import UIError from '@/errors/UIError';
import { LoaderFunctionArgs } from 'react-router';
import { CalibrationTaskFormSchema, PoType } from './zodSchemas';
import { CalibrationOperationType, StartCalibrationTaskRequest } from './types';
import { format, startOfDay } from 'date-fns';
import { queryMeasurementGroup } from '../measurement-group/constants';
import { MeasurementGroupDetails } from '../measurement-group/types';
import apiClient from '@/api';
import { ProcessObject } from '../process-objects/list/types';

export const ALGORITHM_LIST_OPTIONS: { id: number; disabled?: boolean }[] = [
	{ id: 11 },
	{ id: 12 },
	{ id: 14 },
	{ id: 15 },
	{ id: 41, disabled: true }
];
export const CALIBRATION_TASK_ROOT_API_URL = '/api/v1/tasks';

export const PO_TABLE_HEADERS = {
	action: '',
	name: 'PO Name',
	kofFile: 'KOF File',
	id: 'PO ID',
	msId: 'MS ID',
	cx: 'Cx',
	cy: 'Cy',
	timeAdjustment: 'Time adjustment',
	kvClass: 'KV class',
	primaryCalibrationFactor: 'Primary calibration factor',
	ttms: 'TTMS ID',
	algorithm: 'Algorithm number'
};

export function calibrationTaskLoader(queryClient: QueryClient) {
	return async ({ params }: LoaderFunctionArgs) => {
		const measurementGroupId = Number(params.measurementGroupId);

		if (!measurementGroupId || isNaN(measurementGroupId)) {
			throw new UIError('Internal Error: Invalid measurement group Id provided');
		}

		const measurementGroup = await queryClient.ensureQueryData<AxiosResponse<MeasurementGroupDetails>>(
			queryMeasurementGroup(measurementGroupId)
		);

		return {
			measurementGroup
		};
	};
}

export function startCalibrationTask(data: CalibrationTaskFormSchema) {
	const payload = new FormData();

	if (data.referenceData) {
		payload.append('referenceDataFile', data.referenceData);
	}

	if (data.taskType === CalibrationOperationType.CROSS_VALIDATION && data.historicalData) {
		payload.append('historicalDataFile', data.historicalData);
	}

	if (!data.startDate || !data.endDate) {
		throw new UIError('Start and end date are required');
	}

	const metadata = toCalibrationTaskMetadataRequest(data);

	payload.append('metadata', JSON.stringify(metadata));
	payload.append('type', data.taskType);

	return apiClient.post<{ id: string; status: string }>(CALIBRATION_TASK_ROOT_API_URL, payload);
}

const toCalibrationTaskMetadataRequest = (data: CalibrationTaskFormSchema): StartCalibrationTaskRequest['metadata'] => {
	if (data.startDate === null || data.endDate === null) {
		throw new UIError('Start and end date are required');
	}
	const metaData: StartCalibrationTaskRequest['metadata'] = {
		measurementGroupId: data.id,
		startTime: format(startOfDay(data.startDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
		endTime: format(startOfDay(data.endDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
		intercept: data.interceptFlag,
		processObjects: data.poList
	};

	return metaData;
};

export const toCalibrationTaskPo = (po: ProcessObject, mgid: number): PoType => {
	const calibrationFactor = po.relationsDetails.find(rel => rel.mgId === mgid)?.primaryCalibrationFactor ?? 1;
	return {
		id: po.id,
		name: po.name,
		msIds: po.msIds,
		ttmsIdPrimary: po.ttmsIdPrimary,
		ttmsIdSecondary: po.ttmsIdSecondary,
		algorithm: po.algorithm,
		cx: po.cx,
		cy: po.cy,
		timeAdjustment: po.timeAdjustment,
		kvClass: po.kvClass,
		latestKofFile: po.latestKofFile,
		primaryCalibrationFactor: calibrationFactor
	};
};
